import { useState } from 'react';
import type { Alta, Canal, TipoCanal } from '../../api/ops/canales';
import { Boton } from '../../componentes/Boton';
import { Chip } from '../../componentes/Chip';
import { Filtros } from '../../componentes/Filtros';
import { Icono } from '../../componentes/Icono';
import { Tabla } from '../../componentes/Tabla';
import { diaYHora } from '../../logica/fechasCortas';
import { haceCuanto } from '../../logica/tiempo';
import { useCabecera } from '../../marco/cabecera';
import { usePermisos } from '../../permisos/permisos';
import type { PropsApartado } from './Canales';
import { Conectar } from './Conectar';
import { FichaCanal } from './FichaCanal';
import { FichaChatWeb } from './FichaChatWeb';
import { AYUDA_CANALES, conIncidencias, ESTADO_CANAL, FILTROS_CANALES, LINEA_CHAT_WEB, lineaConectarOtro, nombreDe, permitido, saludDe, TIPO, TIPOS, VACIA_CANALES, vivo, type VistaCanales } from './reglas';

/**
 * 11.1 Canales: tabla (en móvil, tarjetas) con canal, local, salud y estado; filtro Conectados · Con incidencias ·
 * Desconectados; «Conectar otro canal» con el bloque que necesita cada tipo; la ficha (`?canal=`) y el alta (11.2).
 * Encargado (`canales.salud.ver` sin `canales.canales.gestionar`, G684): solo ve y comprueba. En solo lectura no se
 * conecta ni se cambia nada; desconectar sí. El chat de la web sale en la lista «incluido con el Asistente IA» y no se
 * ofrece para conectar (G685).
 */
export const RUTA_WIDGETS = '/web?apartado=widgets';
/** «Ir a Mensajes» (G562): el ajuste «Enviar por WhatsApp» está en «Mensajes › Canales de envío» (entrada `mensajes_canales`). */
export const RUTA_CANALES_DE_ENVIO = '/mensajes?apartado=canales';
/** «Ver mis mensajes de WhatsApp» (1.15). */
export const RUTA_PLAN = '/plan';

export function ListaCanales(props: PropsApartado) {
    const { datos, movil, soloLectura, busqueda, navegar, ruta, recargarDatos } = props;
    const { puede } = usePermisos();
    const gestiona = puede('canales.canales.gestionar');
    const conecta = gestiona && !soloLectura;
    const [filtro, setFiltro] = useState<VistaCanales>('conectados');
    const [hecho, setHecho] = useState<string | null>(null);
    const [conectar, setConectar] = useState<{ tipo: TipoCanal | null; reemplaza: Canal | null } | null>(null);
    // Un alta de Meta que se dejó a medias al cerrar el panel: al volver a ese tipo se sigue con ella.
    const [altaAbierta, setAltaAbierta] = useState<Alta | null>(null);

    useCabecera({
        ayuda: AYUDA_CANALES,
        acciones: conecta ? [{ clave: 'conectar', texto: 'Conectar un canal', icono: 'plug', onClick: () => { setHecho(null); setConectar({ tipo: null, reemplaza: null }); } }] : [],
    });

    const abierta = (c: Canal) => datos.abiertas[c.id] ?? null;
    const vivos = datos.canales.filter(vivo);
    const apagados = datos.canales.filter((c) => !vivo(c));
    const conInc = vivos.filter((c) => conIncidencias(c, abierta(c)));
    const lista = filtro === 'incidencias' ? conInc : filtro === 'desconectados' ? apagados : vivos;
    const sede = (id: string | null | undefined) => (id == null ? 'Todos los locales' : (datos.sedes.find((s) => s.id === id)?.nombre ?? ''));
    const base = ruta.split('?')[0] ?? ruta;
    const abrirFicha = (id: string | null) => {
        navegar(id === null ? base : `${base}?canal=${encodeURIComponent(id)}`, true);
    };
    const elegido = datos.canales.find((c) => c.id === busqueda.get('canal')) ?? null;
    const whatsappFunciona = vivos.some((c) => c.channel_type === 'whatsapp' && (c.status === 'active' || c.status === 'degraded'));
    const hace = (iso: string | undefined) => haceCuanto(iso).replace(/^./, (l) => l.toLocaleUpperCase('es'));

    const notaRol = !gestiona
        ? 'Con tu rol ves los canales, su salud y sus incidencias. Conectar, cambiar o desconectar lo hacen el propietario y los administradores.'
        : soloLectura
          ? 'El negocio está en solo lectura: puedes ver la salud y desconectar canales, pero no conectar ni cambiar ninguno.'
          : null;

    return (
        <div className="rv-ca-apartado">
            <Filtros etiqueta="Canales" items={FILTROS_CANALES.map((f) => ({ etiqueta: f.etiqueta }))} valor={FILTROS_CANALES.findIndex((f) => f.valor === filtro)} onCambio={(i) => { setFiltro(FILTROS_CANALES[i]?.valor ?? 'conectados'); setHecho(null); }} />
            {hecho !== null && (
                <div className="rv-ca-hecho" role="status">
                    <span className="rv-ca-hecho__baldosa">
                        <Icono nombre="check" />
                    </span>
                    <span>{hecho}</span>
                </div>
            )}
            {notaRol !== null && (
                <p className="rv-ca-nota">
                    <Icono nombre="eye" />
                    <span>{notaRol}</span>
                </p>
            )}
            {/* G619, G667: sin WhatsApp que funcione, el botón de WhatsApp de la web y de los widgets no se ve. */}
            {!whatsappFunciona && (
                <div className="rv-cn-nota-wa" role="note">
                    <Icono nombre="message-circle" />
                    <span>
                        {vivos.some((c) => c.channel_type === 'whatsapp')
                            ? 'Tu WhatsApp aún no funciona: mientras tanto, el botón de WhatsApp de tu web y de tus widgets no se ve.'
                            : 'Sin un WhatsApp conectado, el botón de WhatsApp de tu web y de tus widgets no se ve. Al conectarlo aparece solo.'}
                    </span>
                    {gestiona && (
                        <Boton tipo="secundario" icono="arrow-right" movil={movil} onClick={() => { navegar(RUTA_WIDGETS); }}>
                            Ver los widgets
                        </Boton>
                    )}
                </div>
            )}
            {lista.length === 0 ? (
                <div className="rv-ca-vacia">
                    <span className="rv-ca-vacia__baldosa">
                        <Icono nombre={VACIA_CANALES[filtro].icono} />
                    </span>
                    <strong>{VACIA_CANALES[filtro].titulo}</strong>
                    <span>{VACIA_CANALES[filtro].texto}</span>
                </div>
            ) : (
                <Tabla
                    etiqueta="Canales del negocio"
                    movil={movil}
                    columnas={[
                        { clave: 'canal', titulo: 'Canal', ancho: 'minmax(230px, 1.6fr)' },
                        { clave: 'local', titulo: 'Local', ancho: 'minmax(120px, 0.8fr)' },
                        { clave: 'salud', titulo: 'Salud', ancho: 'minmax(190px, 1.3fr)' },
                        { clave: 'estado', titulo: 'Estado', ancho: '128px' },
                    ]}
                    filas={lista.map((c) => {
                        const s = saludDe(c, abierta(c), diaYHora, hace, permitido('instagram', datos.bloques).soloListas === true);
                        const tipo = TIPO[c.channel_type] ?? { nombre: c.channel_type, icono: 'messages-square' as const };
                        const porDefecto = c.is_default && vivo(c) && vivos.filter((x) => x.channel_type === c.channel_type).length > 1;
                        const [estado, tono] = ESTADO_CANAL[c.status];
                        return {
                            clave: c.id,
                            etiqueta: nombreDe(c),
                            onAbrir: () => { abrirFicha(c.id); },
                            celdas: {
                                canal: (
                                    <span className="rv-cn-canal">
                                        <span className={`rv-cn-baldosa rv-cn-baldosa--${c.status === 'down' || c.status === 'error' ? 'roja' : vivo(c) ? 'negra' : 'gris'}`}>
                                            <Icono nombre={tipo.icono} />
                                        </span>
                                        <span className="rv-en-celda">
                                            <strong className="rv-cn-nombre">
                                                {nombreDe(c)}
                                                {porDefecto && <Chip estado="neutral">Por defecto</Chip>}
                                                {c.whatsapp_mode === 'business_app' && <Chip estado="neutral">También en la app WhatsApp Business</Chip>}
                                            </strong>
                                            <small>{c.channel_type === 'web_chat' ? LINEA_CHAT_WEB : `${tipo.nombre} · ${c.display_identifier}`}</small>
                                        </span>
                                    </span>
                                ),
                                local: sede(c.location_id),
                                salud: (
                                    <span className="rv-en-celda">
                                        <strong className={`rv-cn-salud rv-cn-salud--${s.tono}`}>{s.linea}</strong>
                                        <small>{s.sub}</small>
                                    </span>
                                ),
                                estado: c.status === 'active' ? '' : <Chip estado={tono}>{estado}</Chip>,
                            },
                        };
                    })}
                />
            )}
            {conecta && filtro === 'conectados' && (
                <section className="rv-cn-otro" aria-label="Conectar otro canal">
                    <div className="rv-ca-bloque__cab">
                        <p className="rv-ca-ceja">Conectar otro canal</p>
                        <span>Puedes tener varios números, cuentas o bots, por ejemplo uno por local.</span>
                    </div>
                    <div className="rv-cn-baldosas">
                        {TIPOS.map((t) => {
                            const p = permitido(t, datos.bloques);
                            const tipo = TIPO[t] ?? { nombre: t, icono: 'messages-square' as const };
                            return (
                                <button key={t} type="button" className="rv-cn-tipo" style={{ opacity: p.ok ? 1 : 0.75 }} aria-label={`Conectar ${tipo.nombre}`} onClick={() => { setHecho(null); setConectar({ tipo: t, reemplaza: null }); }}>
                                    <span className="rv-cn-tipo__cab">
                                        <span className="rv-ca-baldosa">
                                            <Icono nombre={tipo.icono} />
                                        </span>
                                        {p.chip !== null && <Chip estado={p.chip[1]}>{p.chip[0]}</Chip>}
                                    </span>
                                    <strong>{tipo.nombre}</strong>
                                    <small>{lineaConectarOtro(t, p, vivos.filter((c) => c.channel_type === t).length)}</small>
                                </button>
                            );
                        })}
                    </div>
                </section>
            )}
            {elegido !== null && conectar === null && elegido.channel_type === 'web_chat' && <FichaChatWeb key={elegido.id} {...props} canal={elegido} onCerrar={() => { abrirFicha(null); }} />}
            {elegido !== null && conectar === null && elegido.channel_type !== 'web_chat' && (
                <FichaCanal
                    key={elegido.id}
                    {...props}
                    canal={elegido}
                    abierta={abierta(elegido)}
                    onCerrar={() => { abrirFicha(null); }}
                    onReconectar={(c) => { abrirFicha(null); setConectar({ tipo: c.channel_type, reemplaza: c }); }}
                    onDesconectado={(texto) => { abrirFicha(null); setHecho(texto); recargarDatos(); }}
                />
            )}
            {conectar !== null && (
                <Conectar
                    {...props}
                    tipoInicial={conectar.tipo}
                    reemplaza={conectar.reemplaza}
                    altaPrevia={altaAbierta}
                    onAltaAbierta={setAltaAbierta}
                    onCerrar={(texto) => {
                        setConectar(null);
                        if (texto !== undefined) {
                            setHecho(texto);
                        }
                        recargarDatos();
                    }}
                    onVerCanal={(id) => { setConectar(null); recargarDatos(); abrirFicha(id); }}
                />
            )}
        </div>
    );
}
